/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
/**
 * sitac.ts
 * Configurazione e utility per simboli SITAC in DOS518 v2
 * 
 * POSIZIONE: src/config/sitac.ts
 * 
 * @author IRTILAC S.r.l. - DOS518 Development Team
 * @version 1.0
 */

// IMPORTANTE: Il catalogo.json è in public/assets/sitac/
// In Vite, i file in public/ sono serviti dalla root "/"
// Quindi usiamo fetch per caricarlo dinamicamente
// OPPURE lo importiamo come file pubblico

// ============================================================================
// TYPES
// ============================================================================

export type SITACCategory = 
  | 'zona_intervento'
  | 'evoluzione_incendio'
  | 'dispositivo_aereo'
  | 'dispositivo_terrestre'
  | 'azioni_aereo'
  | 'azioni_terra'
  | 'azioni_controfuoco'
  | 'azioni_evacuazione';

export interface SITACSymbol {
  nome: string;
  descrizione: string;
  file: string;
}

export interface SITACSymbolWithCategory extends SITACSymbol {
  categoria: SITACCategory;
}

export interface PlacedSymbol {
  id: string;
  symbol: SITACSymbolWithCategory;
  position: {
    lat: number;
    lng: number;
  };
  rotation?: number;
  scale?: number;
  timestamp: Date;
  notes?: string;
  operatore?: string;
}

export interface SITACCatalog {
  metadata: {
    versione: string;
    data: string;
    fonte: string;
  };
  simboli: Record<SITACCategory, SITACSymbol[]>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const SITAC_CATEGORIES: Record<SITACCategory, string> = {
  zona_intervento: 'Zona di Intervento',
  evoluzione_incendio: 'Evoluzione Incendio',
  dispositivo_aereo: 'Dispositivo Aereo',
  dispositivo_terrestre: 'Dispositivo Terrestre',
  azioni_aereo: 'Azioni Aeree',
  azioni_terra: 'Azioni Terra',
  azioni_controfuoco: 'Controfuoco',
  azioni_evacuazione: 'Evacuazione'
} as const;

export const SITAC_CATEGORY_COLORS: Record<SITACCategory, string> = {
  zona_intervento: '#1976d2',
  evoluzione_incendio: '#d32f2f',
  dispositivo_aereo: '#388e3c',
  dispositivo_terrestre: '#f57c00',
  azioni_aereo: '#7b1fa2',
  azioni_terra: '#c62828',
  azioni_controfuoco: '#e64a19',
  azioni_evacuazione: '#fbc02d'
} as const;

// Schema colori AIB — usato in tutti i layer della mappa SITAC
export const AIB_COLORS = {
  fronteAttivo:      '#ef4444',  // rosso — segmento fronte, massimo pericolo
  retroProtetto:     '#22c55e',  // verde — segmento retro, zona più sicura
  fiancoDestro:      '#f97316',  // arancione — fianco veloce / asse propagazione
  fiancoSinistro:    '#fde047',  // giallo — fianco lento
  perimetroBase:     '#ff4500',  // arancione-rosso — linea perimetro e vertici
  innescoPoint:      '#dc2626',  // rosso acceso — stella punto d'innesco
  assePropagazion:   '#38bdf8',  // azzurro — direttrice vento tratteggiata
  conoPropagazione:  '#ef4444',  // fill triangolo semitrasparente
} as const;

export type AIBColorKey = keyof typeof AIB_COLORS;

// ============================================================================
// CATALOGO - Caricato da file pubblico
// ============================================================================

// Cache del catalogo
let catalogoCache: SITACCatalog | null = null;

/**
 * Carica il catalogo SITAC dal file pubblico
 * NOTA: Questa è una funzione async perché carica da public/
 */
export const loadCatalogo = async (): Promise<SITACCatalog> => {
  if (catalogoCache) {
    console.log('📦 SITAC: Usando catalogo dalla cache');
    console.log('📋 Versione cache:', catalogoCache.metadata.versione);
    return catalogoCache;
  }

  try {
    console.log('🔄 SITAC: Caricamento catalogo da /assets/sitac/catalogo.json...');
    const response = await fetch('/assets/sitac/catalogo.json');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    catalogoCache = await response.json();
    
    // LOG IMPORTANTI PER DEBUG
    console.log('✅ SITAC: CATALOGO CARICATO CON SUCCESSO!');
    console.log('📋 Versione:', catalogoCache.metadata.versione);
    console.log('📋 Data:', catalogoCache.metadata.data);
    console.log('📋 Fonte:', catalogoCache.metadata.fonte);
    console.log('🔢 Totale categorie:', Object.keys(catalogoCache.simboli).length);
    
    // Mostra primo simbolo di ogni categoria per verifica
    Object.entries(catalogoCache.simboli).forEach(([cat, simboli]) => {
      if (simboli.length > 0) {
        console.log(`  • ${cat}: ${simboli.length} simboli - Primo: ${simboli[0].file}`);
      }
    });
    
    return catalogoCache!;
  } catch (error) {
    console.error('❌ SITAC: Errore caricamento catalogo:', error);
    throw error;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Ottiene il percorso completo di un simbolo SITAC
 */
export const getSymbolPath = (category: SITACCategory, filename: string): string => {
  return `/assets/sitac/${category}/${filename}`;
};

/**
 * Ottiene i simboli di una categoria o tutti
 * NOTA: Questa funzione ora richiede che il catalogo sia stato caricato
 */
export const getSITACSymbols = (category?: SITACCategory): SITACSymbol[] => {
  if (!catalogoCache) {
    console.warn('Catalogo SITAC non ancora caricato! Chiama loadCatalogo() prima.');
    return [];
  }
  
  if (category) {
    return catalogoCache.simboli[category] || [];
  }
  // Ritorna tutti i simboli appiattiti in un array
  return Object.values(catalogoCache.simboli).flat();
};

/**
 * Cerca simboli per query testuale
 */
export const searchSITACSymbols = (query: string): SITACSymbolWithCategory[] => {
  if (!catalogoCache) {
    console.warn('Catalogo SITAC non ancora caricato!');
    return [];
  }

  const results: SITACSymbolWithCategory[] = [];
  const lowerQuery = query.toLowerCase().trim();
  
  if (!lowerQuery) return results;

  Object.entries(catalogoCache.simboli).forEach(([category, symbols]) => {
    symbols.forEach(symbol => {
      if (
        symbol.nome.toLowerCase().includes(lowerQuery) ||
        symbol.descrizione.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          ...symbol,
          categoria: category as SITACCategory
        });
      }
    });
  });

  return results;
};

/**
 * Ottiene statistiche sul catalogo SITAC
 */
export const getSITACStats = () => {
  if (!catalogoCache) {
    return {
      totaleSimboliPerCategoria: {} as Record<SITACCategory, number>,
      totaleSimboliComplessivo: 0,
      categorie: 0,
      metadata: {
        versione: 'N/A',
        data: 'N/A',
        fonte: 'N/A'
      }
    };
  }

  const stats = {
    totaleSimboliPerCategoria: {} as Record<SITACCategory, number>,
    totaleSimboliComplessivo: 0,
    categorie: Object.keys(SITAC_CATEGORIES).length,
    metadata: catalogoCache.metadata
  };

  Object.entries(catalogoCache.simboli).forEach(([category, symbols]) => {
    stats.totaleSimboliPerCategoria[category as SITACCategory] = symbols.length;
    stats.totaleSimboliComplessivo += symbols.length;
  });

  return stats;
};

/**
 * Ottiene il colore di una categoria
 */
export const getCategoryColor = (category: SITACCategory): string => {
  return SITAC_CATEGORY_COLORS[category];
};

/**
 * Ottiene il nome leggibile di una categoria
 */
export const getCategoryLabel = (category: SITACCategory): string => {
  return SITAC_CATEGORIES[category];
};

/**
 * Genera un ID univoco per un simbolo posizionato
 */
export const generateSymbolId = (): string => {
  return `sitac_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Esporta una mappa SITAC in formato JSON
 */
export const exportSITACMap = (
  symbols: PlacedSymbol[],
  metadata?: {
    incidentId?: string;
    operatore?: string;
    location?: string;
  }
) => {
  return {
    version: '1.0',
    formato: 'SITAC-DOS518',
    dataCreazione: new Date().toISOString(),
    metadata: {
      ...metadata,
      totaleSimboliSegnati: symbols.length
    },
    simboli: symbols.map(s => ({
      ...s,
      timestamp: s.timestamp.toISOString()
    }))
  };
};

// Per compatibilità con codice esistente
export default {
  loadCatalogo,
  getSITACSymbols,
  getSymbolPath,
  searchSITACSymbols,
  getSITACStats,
  getCategoryColor,
  getCategoryLabel,
  generateSymbolId,
  exportSITACMap,
  SITAC_CATEGORIES,
  SITAC_CATEGORY_COLORS
};
